import { I18nextProvider, i18nConfig } from '@cobliteam/i18n';
import { PermissionsProvider } from '@cobliteam/permissions';
import { Environment } from '@cobliteam/react-environments';
import Alert, { AlertContainer } from '@cobliteam/ui/components/Alert';
import { TooltipProvider } from '@cobliteam/ui/components/Tooltip';
import { basicLogger } from '@launchdarkly/js-client-sdk';
import { createLDReactProvider } from '@launchdarkly/react-sdk';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConnectedRouter } from 'connected-react-router';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { Provider as ReduxProvider } from 'react-redux';
import DemoBanner from './components/DemoBanner/DemoBanner';
import Router from './Router';
import { history, store } from './store';

const env = Environment.getInstance().getEnvironment();

const IS_DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false, // Never change data on mount to true - Loop Fetch on map.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 3,
      retryDelay: 1000,
      staleTime: 1000 * 60 * 5,
    },
  },
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.successMessage) {
        Alert.success((mutation.meta?.successMessage as string) || 'Sucesso ao carregar dados');
      }
    },
    onError: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.errorMessage) {
        Alert.error((mutation.meta?.errorMessage as string) || 'Erro ao carregar dados');
      }
    },
  }),
  queryCache: new QueryCache({
    onSuccess: (_data, query) => {
      if (query.meta?.successMessage) {
        Alert.success((query.meta?.successMessage as string) || 'Sucesso ao carregar dados');
      }
    },
    onError: (_data, query) => {
      if (query.meta?.errorMessage) {
        Alert.error((query.meta?.errorMessage as string) || 'Erro ao carregar dados');
      }
    },
  }),
});

const Dashboard = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ReduxProvider store={store}>
        <I18nextProvider i18n={i18nConfig}>
          <TooltipProvider delayDuration={300}>
            <PermissionsProvider>
              <ConnectedRouter history={history}>
                <NuqsAdapter>
                  <Router />
                  <AlertContainer richColors position='top-right' pauseWhenPageIsHidden />
                  <DemoBanner />
                </NuqsAdapter>
              </ConnectedRouter>
            </PermissionsProvider>
          </TooltipProvider>
        </I18nextProvider>
      </ReduxProvider>
    </QueryClientProvider>
  );
};

// v4 substitui o HOC `withLDProvider` por um Provider retornado por `createLDReactProvider`.
// O contexto inicial anônimo é obrigatório na assinatura — o `FeatureFlagsProvider` faz o
// `ldClient.identify()` real com user+fleet assim que a sessão resolve.
// Cache de flags em localStorage desabilitado (`maxCachedContexts: 0`): o SDK não lê nem
// escreve snapshots locais (e pruna os deixados por versões anteriores) — a cada boot as
// flags vêm frescas do LD e o `FeatureFlagsProvider` segura o loading até o identify.
const LDProvider = createLDReactProvider(
  env.launchDarklyId,
  { kind: 'user', anonymous: true, key: 'anonymous' },
  {
    ldOptions: {
      logger: basicLogger({ level: 'none' }),
      sendEvents: !IS_DEMO_MODE,
      maxCachedContexts: 0,
      // Demo: LaunchDarkly is fully mocked by MSW (libs/msw-demo). Force polling
      // (`streaming: false`) so the SDK only issues eval GETs — MSW can't
      // intercept EventSource streaming, which would hit real LD and
      // reconnect-loop. Analytics/diagnostics are silenced so nothing escapes to
      // `*.launchdarkly.com`.
      ...(IS_DEMO_MODE && { streaming: false, diagnosticOptOut: true }),
    },
  }
);

// O provider é montado em TODOS os modos, inclusive demo. Em demo o `.env.demo` passa
// `REACT_APP_LAUNCH_DARKLY_ID=demo-dummy`, o provider roda em polling e o MSW responde o
// endpoint de eval (ver libs/msw-demo/src/handlers/launchDarkly.ts); o `FeatureFlagsProvider`
// curto-circuita o `identify()`. Montar sempre é obrigatório porque `useLDClient()`/`useFlags()`
// (LD React SDK v4) fazem `useContext` + destructuring do contexto; sem provider o contexto é
// `null` e o app quebra na primeira leitura de flag.
const DashboardWithLaunchDarkly = () => (
  <LDProvider>
    <Dashboard />
  </LDProvider>
);

export default DashboardWithLaunchDarkly;
